/**
 * What the prototype saves: contracts made in the builder (for the API mock to
 * answer with), the tracks linked to them, and what the API has no field for
 * yet — format splits, per-payee advances, and sub-contracts. Kept in session
 * storage, so a refresh keeps them; a new tab starts empty.
 *
 * Scaffolding — delete with the rest of src/prototypes/modular-contract-builder/.
 */
import type { Advances } from "@/components/features/contracts/contract-builder/advances";
import type { ExpenseSplit } from "@/components/features/contracts/contract-builder/AddedSections";
import { CONTRACT_EXTRAS, SUB_CONTRACTS } from "./fixtures";
import type { FormatRule } from "@/components/features/contracts/contract-builder/formatRules";
import type { TrackRule } from "@/components/features/contracts/contract-builder/trackRules";
import type {
  ArtistPoolShares,
  PoolRow,
} from "@/components/features/contracts/contract-builder/PoolSplits";
import type { ExpenseRow } from "@/components/features/contracts/contract-builder/ExpensePoolSplits";
import type { SubContractPayee } from "@/components/features/contracts/contract-builder/sub-contract/SubContractSections";

/** What a contract made in the builder holds beyond the API's fields. */
export type ContractExtras = {
  formatRules: FormatRule[];
  /** Splits for some tracks, in place of the Revenue split. */
  trackRules?: TrackRule[];
  advances: Advances;
  /** Payees paid an advance who aren't in the Revenue split, with their names. */
  advancePayees?: AdvancePayee[];
  /** Payees taken off the Advances table: hidden there, still on the contract. */
  hiddenAdvancePayees?: string[];
  /** Who carries the expenses: the label's share and each payee's. */
  expenseSplit?: ExpenseSplit;
  /** The Label and artist pool splits: all formats, then the exceptions. */
  poolRows?: PoolRow[];
  /** The Artist pool splits: each payee's share of the pool, by track. */
  artistShares?: ArtistPoolShares;
  /** The Expense split by expense type: all expenses, then the types added. */
  expenseRows?: ExpenseRow[];
};

export type AdvancePayee = { id: string; name: string };

/** A sub-contract made in the sub-contract builder, under its source contract. */
export type SavedSubContract = {
  id: string;
  name: string;
  contractId: string;
  trackIds: string[];
  payees: SubContractPayee[];
  advances: Advances;
  advancePayees?: AdvancePayee[];
  /** Payees taken off the Advances table: hidden there, still on the contract. */
  hiddenAdvancePayees?: string[];
  expenseSplit?: ExpenseSplit;
  createdAt: string;
};

type Store = {
  /** Payees created in the prototype (the Add payee drawer), as the API returns them. */
  payees: Record<string, unknown>[];
  /** Contracts as POSTed to the API, with the id the mock gave them. */
  contracts: Record<string, Record<string, unknown>>;
  /** Contract id → the recordings linked to it. */
  tracks: Record<string, string[]>;
  extras: Record<string, ContractExtras>;
  subContracts: SavedSubContract[];
  /** Contracts deleted in the prototype — sample ones too: no longer listed or found. */
  deleted: string[];
};

const KEY = "modular-contract-builder-prototype";
const empty = (): Store => ({
  payees: [],
  contracts: {},
  tracks: {},
  extras: {},
  subContracts: [],
  deleted: [],
});

function read(): Store {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? { ...empty(), ...JSON.parse(raw) } : empty();
  } catch {
    return empty();
  }
}

function write(change: (store: Store) => void) {
  const store = read();
  change(store);
  try {
    sessionStorage.setItem(KEY, JSON.stringify(store));
  } catch {
    // Storage off or full: this session just won't remember it.
  }
}

export const savedContracts = () => Object.values(read().contracts);
export const savedContract = (id: string) => read().contracts[id];
export const saveContract = (contract: Record<string, unknown> & { id: string }) =>
  write((s) => {
    s.contracts[contract.id] = contract;
  });

/** The recordings linked to a contract, or undefined when none were ever linked here. */
export const linkedTracks = (contractId: string): string[] | undefined => read().tracks[contractId];
export const linkTracks = (contractId: string, recordingIds: string[]) =>
  write((s) => {
    // The tracks just linked first: what's added shows at the top.
    s.tracks[contractId] = [...new Set([...recordingIds, ...(s.tracks[contractId] ?? [])])];
  });

/** A contract's extras: as saved here, else the sample contracts' own. */
export const contractExtras = (contractId: string): ContractExtras | undefined =>
  read().extras[contractId] ?? CONTRACT_EXTRAS[contractId];
export const saveContractExtras = (contractId: string, extras: ContractExtras) =>
  write((s) => {
    s.extras[contractId] = extras;
  });

export const subContractsOf = (contractId: string) =>
  [...SUB_CONTRACTS, ...read().subContracts].filter((s) => s.contractId === contractId);
export const saveSubContract = (subContract: Omit<SavedSubContract, "id" | "createdAt">) =>
  write((s) => {
    s.subContracts.push({
      ...subContract,
      id: `sub-contract-${Date.now()}`,
      createdAt: new Date().toISOString(),
    });
  });

export const setLinkedTracks = (contractId: string, recordingIds: string[]) =>
  write((s) => {
    s.tracks[contractId] = recordingIds;
  });
export const unlinkTrack = (contractId: string, recordingId: string) =>
  write((s) => {
    s.tracks[contractId] = (s.tracks[contractId] ?? []).filter((id) => id !== recordingId);
  });

export const deletedContracts = () => read().deleted;
export const deleteContract = (contractId: string) =>
  write((s) => {
    s.deleted = [...new Set([...s.deleted, contractId])];
    delete s.contracts[contractId];
  });

export const savedPayees = () => read().payees;
export const savePayee = (payee: Record<string, unknown>) =>
  write((s) => {
    s.payees.push(payee);
  });
